import { Eye, Target, HeartStraight, Quotes, CheckCircle } from "@phosphor-icons/react";
import { SCHOOLS } from "./siteData";
import { useSettings, useSite } from "../../lib/site";

const Kicker = ({ children, light }) => (
  <div className={`text-xs font-semibold tracking-[0.2em] uppercase ${light ? "text-[#F5B83D]" : "text-[#E39A0B]"}`}>{children}</div>
);

const StatStrip = ({ items }) => (
  <div className="flex flex-wrap gap-3 mt-5" data-testid="about-stats">
    {items.map((a) => (
      <div key={a.id} className="bg-blue-50 border border-blue-100 rounded-full px-4 py-2 text-sm">
        <span className="font-display font-black text-[#0B3A8C]">{a.value}</span>{" "}
        <span className="text-zinc-700">{a.label}</span>
      </div>
    ))}
  </div>
);

const Intro = ({ settings, achievements, schools }) => (
  <div className="grid lg:grid-cols-2 gap-10 items-start">
    <div className="space-y-4">
      <img src="/site/g1.jpg" alt="Students in class" className="rounded-2xl w-full h-72 object-cover" />
      {schools.map((s) => (
        <div key={s.slug} className="border border-zinc-200 rounded-xl p-4" data-testid={`about-school-${s.slug}`}>
          <div className="font-display font-bold text-[#0B2A5C]">{s.name}</div>
          <div className="text-sm text-zinc-600">{[s.details, s.address].filter(Boolean).join(" · ")}</div>
        </div>
      ))}
    </div>
    <div>
      <Kicker>About Us</Kicker>
      <h2 className="font-display text-3xl md:text-4xl font-black text-[#0B2A5C] mt-2">About Our Schools</h2>
      <StatStrip items={achievements} />
      <p className="text-zinc-700 mt-6 whitespace-pre-line leading-relaxed" data-testid="about-text">{settings?.about_text}</p>
    </div>
  </div>
);

const Vision = ({ text }) => (
  <div className="bg-[#0B2A5C] text-white rounded-2xl p-8 h-full flex flex-col justify-center" data-testid="about-vision">
    <Eye size={36} weight="duotone" className="text-[#F5B83D]" />
    <Kicker light>Our Vision</Kicker>
    <p className="font-display text-xl md:text-2xl font-bold mt-3 leading-snug">{text}</p>
  </div>
);

const Mission = ({ items }) => (
  <div data-testid="about-mission">
    <div className="flex items-center gap-2"><Target size={28} weight="duotone" className="text-[#0B3A8C]" /><Kicker>Our Mission</Kicker></div>
    <div className="grid sm:grid-cols-2 gap-3 mt-4">
      {items.map((m, i) => (
        <div key={i} className="border border-zinc-200 rounded-xl p-4 bg-white" data-testid={`about-mission-${i}`}>
          <div className="flex items-center gap-2 font-display font-bold text-[#0B2A5C]">
            <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" />{m.title}
          </div>
          <p className="text-sm text-zinc-600 mt-1">{m.text}</p>
        </div>
      ))}
    </div>
  </div>
);

const Commitment = ({ text, motto }) => (
  <div className="grid lg:grid-cols-3 gap-6 items-stretch">
    {text && (
      <div className="lg:col-span-2 bg-amber-50 border border-amber-100 rounded-2xl p-8" data-testid="about-commitment">
        <div className="flex items-center gap-2"><HeartStraight size={26} weight="duotone" className="text-[#E39A0B]" /><Kicker>Our Commitment to Every Child</Kicker></div>
        <p className="text-zinc-800 mt-3 leading-relaxed">{text}</p>
      </div>
    )}
    {motto && (
      <div className="bg-[#E39A0B] text-white rounded-2xl p-8 flex flex-col justify-center" data-testid="about-motto">
        <Quotes size={32} weight="fill" />
        <div className="text-xs font-semibold tracking-[0.2em] uppercase mt-2 opacity-90">Our Motto</div>
        <p className="font-display text-xl font-black mt-1 leading-snug">{motto}</p>
      </div>
    )}
  </div>
);

const Correspondent = ({ message, quote }) => (
  <div className="grid lg:grid-cols-5 gap-6" data-testid="about-correspondent">
    <div className="lg:col-span-2 bg-blue-50 border border-blue-100 rounded-2xl p-8 lg:sticky lg:top-24 self-start">
      <Kicker>Message from the Correspondent</Kicker>
      <h3 className="font-display text-2xl md:text-3xl font-black text-[#0B2A5C] mt-2">Learning with purpose, growing with confidence</h3>
      {quote && (
        <blockquote className="mt-6 border-l-4 border-[#E39A0B] pl-4 italic text-zinc-700" data-testid="correspondent-quote">
          &ldquo;{quote}&rdquo;
        </blockquote>
      )}
    </div>
    <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-2xl p-8">
      <p className="text-zinc-700 whitespace-pre-line leading-relaxed" data-testid="correspondent-message">{message}</p>
      <div className="mt-6 text-sm" data-testid="correspondent-signature">
        <div className="text-zinc-500">With best wishes,</div>
        <div className="font-display font-bold text-[#0B2A5C] mt-1">Correspondent</div>
        {SCHOOLS.map((s) => <div key={s.slug} className="text-zinc-600">{s.name}</div>)}
      </div>
    </div>
  </div>
);

export const About = () => {
  const { settings } = useSettings();
  const { items: achievements } = useSite("achievements");
  const bySlug = Object.fromEntries((settings?.schools || []).map((s) => [s.slug, s]));
  const schools = SCHOOLS.map((s) => ({ ...s, ...bySlug[s.slug] }));

  return (
    <section id="about" className="py-20 px-4 bg-white scroll-mt-24">
      <div className="max-w-6xl mx-auto space-y-14">
        <Intro settings={settings} achievements={achievements} schools={schools} />
        {settings?.correspondent_message && (
          <Correspondent message={settings.correspondent_message} quote={settings.correspondent_quote} />
        )}
        {(settings?.vision || settings?.mission?.length > 0) && (
          <div className="grid lg:grid-cols-5 gap-6">
            {settings.vision && <div className="lg:col-span-2"><Vision text={settings.vision} /></div>}
            {settings.mission?.length > 0 && <div className="lg:col-span-3"><Mission items={settings.mission} /></div>}
          </div>
        )}
        <Commitment text={settings?.commitment} motto={settings?.motto} />
      </div>
    </section>
  );
};
